<template>
    <div>
        <page-header>
            Group Collections
            <template v-slot:actions>
                <vx-button @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="sm">
                    <span class="vx-hidden sm:vx-inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon class="vx-hidden sm:vx-inline-block"
                        :icon="faQuestionCircle"></font-awesome-icon>
                    <font-awesome-icon class="vx-inline-block sm:vx-hidden"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <group-collections-overview v-if="showOverview"></group-collections-overview>

        <vx-alert v-if="list.length >= 20"
            :closeable="false"
            shadow
            class="vx-mt-14 vx-mb-8">
            Reached maximum of 20 collections
        </vx-alert>

        <group-collection-controls v-model="filters"
            @apply="applyFilters"
            @reset="resetFilters"
            :list="list"
            :loading="loading"></group-collection-controls>

        <page-loading v-if="loading && !list.length"></page-loading>
        <vx-box v-else-if="!loading && !list.length"
            color="info"
            class="vx-mt-4 vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500">
            No Group Collections yet.
            <vx-button v-if="isAdminOrOwner"
                :href="`/settings/teams/${teamId}/group-collections/new`">
                New Collection
                <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </vx-box>
        <vx-box v-else-if="!filteredList.length"
            color="info"
            class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500">
            <div class="vx-w-64 vx-mx-auto vx-text-center">
                No results for current search, try adjusting your filters.
            </div>
            <vx-button type="button"
                color="secondary"
                @click="resetFilters">
                Reset All <font-awesome-icon :icon="faCircleX" />
            </vx-button>
        </vx-box>

        <vx-table v-else-if="list.length"
            class="xl:vx-grid-cols-3-auto"
            rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>
                    Name
                </div>
                <div>
                    Groups
                </div>
            </vx-table-row>

            <group-collection-row v-for="(item) in filteredList"
                :modelValue="item"
                :key="item.id"
                @deleted="removeFromList($event)"></group-collection-row>
        </vx-table>
    </div>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faQuestionCircle,
    faCircleX,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupCollectionControls from './GroupCollectionControls.vue';
import GroupCollectionsOverview from './GroupCollectionsOverview.vue';
import GroupCollectionRow from './GroupCollectionRow.vue';
import groupCollectionsClient from '~/services/groupCollectionsClient';
import { computed } from 'vue';
import { useGroupCollectionsGroupsCount } from '~/composables/useGroupCollectionsGroupsCount';

const showOverview = ref(true);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const loading = ref(false);
const filters = ref({});
const list = ref([]);

const filteredList = computed(() => {
    return list.value.filter((item) => {
        const name = item.name.toLocaleLowerCase().includes(filters.value.query.toLocaleLowerCase());

        return name;
    }).sort((a, b) => {
        if (filters.value.sort_field === 'name') {
            return filters.value.sort_dir === 'desc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        } else if (['created_at', 'updated_at'].includes(filters.value.sort_field)) {
            return filters.value.sort_dir === 'desc' ? new Date(b[filters.value.sort_field]) - new Date(a[filters.value.sort_field]) : new Date(a[filters.value.sort_field]) - new Date(b[filters.value.sort_field]);
        } else {
            return 0;
        }
    })
});

const defaultFilters = () => ({
    query: '',
    sort_field: 'created_at',
    sort_dir: 'desc'
});

const resetFilters = () => {
    filters.value = defaultFilters();
};

const applyFilters = () => {
    filters.value = {
        ...filters.value,
    }
}

onMounted(() => {
    resetFilters();
    loadData();
});

const { fetchGroupCollectionsGroupsCount } = useGroupCollectionsGroupsCount();

const loadData = async () => {
    try {
        loading.value = true;
        const response = await groupCollectionsClient.listGroupCollections(teamId, { ...filters.value });
        list.value = response.data.data;

        fetchGroupCollectionsGroupsCount(list.value.map((groupCollection) => groupCollection.id));

        loading.value = false;
    } catch (e) {
        console.error(e);
    }
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

</script>
